import {WinPresentationController} from "../../WinPresentationController";
import {TimelineLite} from "gsap";
import {WalkOfShame} from "../../../WalkOfShame";
import {WalkOfShameGameConfig} from "../../../WalkOfShameGameConfig";
import {BelowStakeWinRestriction} from "../../../server/BelowStakeWinRestriction";
import {IBetWinData} from "../../../server/data/IWalkOfShameData";
import {WalkOfShameParsedGameData} from "../../../server/data/WalkOfShameParsedGameData";
import {WalkOfShameSound} from "../../../sound/WalkOfShameSound";

export class InitialWinController extends WinPresentationController {

    private _mainTimeline: TimelineLite | null;
    private _belowStakeWinRestriction: boolean;

    constructor() {
        super("InitialWinController", true);
    }

    private static getReelDelay(winRatio: number): number {
        // tiny/small
        if (winRatio < WalkOfShameGameConfig.WIN_RATIOS[2].ratio) {
            return 0;
        }

        //mid
        if (winRatio < WalkOfShameGameConfig.WIN_RATIOS[3].ratio) {
            return 0.05;
        }

        // large and up
        return 0.1;
    }

    protected hasWin(): boolean {
        return WalkOfShame.model.parsedGameData.betWins.length > 0;
    }

    protected async createWinAnimation(): Promise<any> {
        this._belowStakeWinRestriction = BelowStakeWinRestriction.isWinBelowPlayedBetValue();
        const tl: TimelineLite = new TimelineLite();

        if (BelowStakeWinRestriction.isWinBelowPlayedBetValue()) {
            tl.add(() => WalkOfShame.keypadPlugin.setWin(WalkOfShame.model.parsedGameData.totalWin, WalkOfShame.model.parsedGameData.showTotalWin, BelowStakeWinRestriction.isWinBelowStake()));
        } else {
            tl.add([
                this.getWinAnimation(),
                this.getWinCountUpAnimation()
            ]);
        }
        this._mainTimeline = tl;
        await tl;
        this._mainTimeline = null;
    }

    protected getWinAnimation(): TimelineLite {
        const winRatio: number = WalkOfShame.model.parsedGameData.currentWinRatioConfig?.ratio!,
            tl: TimelineLite = new TimelineLite();

        WalkOfShame.model.parsedGameData.betWins.forEach((w: IBetWinData) => {
            tl.add(this.getSingleBetWayWinAnimation(w, winRatio), 0);
        });
        return tl;
    }

    protected getSingleBetWayWinAnimation(winData: IBetWinData, winRatio: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            reelDelay: number = InitialWinController.getReelDelay(winRatio);
        let symbolDelay: number = 0,
            winSweepWildPlayed: boolean = false;

        // #164
        if (winRatio >= 15) {
            tl.add(() => {
                WalkOfShameSound.soundPlayer.playTrack({
                    name: WalkOfShameSound.WIN_SWEEP_SYM
                });
            }, 0);
        }
        winData.reelWinPositions.forEach((reelWin: boolean[], reelId: number) => {
            let hasReelWin = false;
            reelWin.forEach((winPosition: boolean, symId: number) => {
                if (winPosition) {
                    hasReelWin = true;

                    // #164
                    if (!winSweepWildPlayed && winRatio >= 15 && WalkOfShame.mToWild.ifFullSymExistsOnReel(reelId)) {
                        winSweepWildPlayed = true;
                        tl.add(() => {
                            WalkOfShameSound.soundPlayer.playTrack({
                                name: WalkOfShameSound.WIN_SWEEP_WILD
                            });
                        }, symbolDelay);
                    }

                    tl.add(WalkOfShame.getWinAnimationForSymbol(reelId, symId + 1, true), symbolDelay);
                }
            });
            if (hasReelWin) {
                symbolDelay += reelDelay;
            }
        });

        return tl;
    }

    protected getWinCountUpAnimation(): TimelineLite {
        const data: WalkOfShameParsedGameData = WalkOfShame.model.parsedGameData,
            tl: TimelineLite = new TimelineLite();

        // #183
        if (WalkOfShame.apiPlugin.gameClientConfiguration.displayIndividualWinsInGame) {
            tl.add(WalkOfShame.winWays.showWinAmountNWaysOnInitialWin(WalkOfShame.model.parsedGameData.betWins, this._belowStakeWinRestriction), 0);
        }

        if (data.currentWinRatioConfig!.bigWinLevel > -1) {
            return tl; // no count up for big win.
        }

        tl.add(WalkOfShame.initialCountUp.getCountUpAnimation(0, data.singleWin, data.currentWinRatioConfig!.duration, data.currentMultiplier));
        tl.add(() => WalkOfShame.keypadPlugin.setWin(data.totalWin, data.showTotalWin, BelowStakeWinRestriction.isWinBelowStake()));

        return tl;
    }

    protected skip(): void {
        // #152
        WalkOfShame.initialCountUp.isAborted = true;

        this._mainTimeline?.progress(1);
    }
}